import {
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
  WorkflowCapabilityReference,
  WorkflowStepDeclaration,
  requireWorkflowPayloadVersion,
} from './platform-workflow-definition-lifecycle.models';
import {
  addWorkflowCapability,
  installedReference,
  WorkflowCapabilityCatalogEntry,
  sameCapabilityReference,
} from './workflow-capability-authoring';

export type WorkflowInstalledPurpose = 'preparation' | 'review';

const REVIEW_REQUIREMENTS = [
  'review.current-request',
  'review.independent-reviewer',
  'review.changes-reason',
];
const purposeCodes = {
  preparation: ['workflow.preparation.submit'],
  review: ['workflow.review.request', 'workflow.review.submit'],
};
const purposeView = {
  preparation: 'workflow.preparation.work',
  review: 'workflow.review.records',
};

/** Classifies only a complete installed command set; a declaration reference is not a purpose. */
export function installedStepPurpose(
  node: WorkflowDefinitionNode | null | undefined,
): WorkflowInstalledPurpose | null {
  const commands = node?.declaration?.behavior?.commands;
  if (!Array.isArray(commands)) return null;
  return (
    (['preparation', 'review'] as const).find(
      (purpose) =>
        commands.length === purposeCodes[purpose].length &&
        purposeCodes[purpose].every(
          (code) =>
            commands.filter(
              (command) =>
                command?.capability?.key?.provider === 'workflow' &&
                command.capability.key.kind === 'command' &&
                command.capability.key.code === code &&
                command.capability.key.version === 1,
            ).length === 1,
        ),
    ) ?? null
  );
}

export interface WorkflowPurposeChangePreview {
  nodeId: string;
  from: WorkflowInstalledPurpose | null;
  target: WorkflowInstalledPurpose;
  retained: string[];
  replaced: string[];
  unresolved: string[];
  blocked: string[];
  document: WorkflowDefinitionDocument | null;
}

/** Plans a local declaration change only. Graph repair and release validation remain explicit. */
export function previewInstalledPurposeChange(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  target: WorkflowInstalledPurpose,
  catalog: WorkflowCapabilityCatalogEntry[],
): WorkflowPurposeChangePreview {
  const matches = document.nodes.filter((node) => node?.id === nodeId);
  const node = matches.length === 1 ? matches[0] : null;
  if (
    node?.typeCode !== 'human_step' ||
    !node.declaration ||
    !hasEditableRouting(document)
  )
    throw new Error('Select one configured human step with editable routing.');
  const declaration = node.declaration;
  const from = installedStepPurpose(node);
  const preview: WorkflowPurposeChangePreview = {
    nodeId,
    from,
    target,
    document: null,
    retained: [
      'Step identity, name, position and responsibility.',
      'Every graph node and path, earlier decisions, and workflow-wide settings.',
      'Detail layout, content blocks, additional dashboards and existing capability selections.',
    ],
    replaced: [],
    unresolved: [],
    blocked: [],
  };
  if (!from)
    preview.blocked.push(
      'This step does not have one complete supported purpose. Custom, mixed or incomplete commands are preserved; review them before changing purpose.',
    );
  if (from === target)
    preview.blocked.push('This step already uses the selected purpose.');
  if (!from || from === target) return preview;
  if (
    !declaration.detail ||
    !Array.isArray(declaration.detail.blocks) ||
    !Array.isArray(declaration.behavior?.commands)
  ) {
    preview.blocked.push(
      'Incomplete saved detail or behavior settings must be repaired before changing purpose.',
    );
    return preview;
  }
  const select = (
    provider: string,
    kind: string,
    code: string,
  ): WorkflowCapabilityReference => {
    const entries = catalog.filter(
      (entry) =>
        entry.key.provider === provider &&
        entry.key.kind === kind &&
        entry.key.code === code &&
        entry.key.version === 1,
    );
    if (entries.length !== 1)
      throw new Error(`The installed ${code} contract is unavailable.`);
    updated = addWorkflowCapability(updated, entries[0], catalog);
    return installedReference(entries[0]);
  };
  let updated: WorkflowDefinitionDocument = {
    ...document,
    payloadSchemaVersion: requireWorkflowPayloadVersion(
      document.payloadSchemaVersion,
      '1.7',
    ),
    triggers: document.triggers ?? [],
    numbering: document.numbering ?? { mode: 'disabled' },
  };
  for (const command of declaration.behavior.commands) {
    const code = command.capability.key.code;
    const installed = select('workflow', 'command', code);
    if (!sameCapabilityReference(command.capability, installed))
      preview.blocked.push(
        `${code} has an incompatible saved contract. Its evidence will not be discarded.`,
      );
    if (
      unknownKeys(command, ['capability', 'requirements', 'outcomes']).length ||
      unknownReferenceSettings(command.capability)
    )
      preview.blocked.push(
        `${code} has additional saved settings that this purpose editor cannot replace.`,
      );
    const requirements =
      code === 'workflow.review.submit' ? REVIEW_REQUIREMENTS : [];
    if (
      !Array.isArray(command.requirements) ||
      !sameCodes(command.requirements, requirements)
    )
      preview.blocked.push(
        `${code} has custom or incomplete requirements. They must be reviewed before replacement.`,
      );
    const outcomes = command.outcomes;
    if (
      !Array.isArray(outcomes) ||
      (code !== 'workflow.review.submit' && outcomes.length !== 0) ||
      (code === 'workflow.review.submit' &&
        (outcomes.length !== 2 ||
          !['approved', 'changes_required'].every(
            (outcomeCode) =>
              outcomes.filter((outcome) => outcome?.code === outcomeCode)
                .length === 1,
          )))
    ) {
      preview.blocked.push(
        `${code} has custom or incomplete outcomes that will not be removed automatically.`,
      );
    } else if (
      outcomes.some(
        (outcome) =>
          unknownKeys(outcome, [
            'code',
            'label',
            'reasonPolicy',
            'destinationNodeId',
          ]).length || !['optional', 'required'].includes(outcome.reasonPolicy),
      )
    ) {
      preview.blocked.push(
        `${code} has unsupported outcome settings that must be reviewed first.`,
      );
    }
  }
  const sourceViewCode = purposeView[from];
  const dashboards = declaration.dashboards ?? [];
  if (!Array.isArray(dashboards)) {
    preview.blocked.push('The saved dashboard collection is malformed.');
    return preview;
  }
  const sourceDashboards = dashboards.filter(
    (dashboard) =>
      dashboard?.view?.key?.provider === 'platform' &&
      dashboard.view.key.kind === 'view' &&
      dashboard.view.key.code === sourceViewCode &&
      dashboard.view.key.version === 1,
  );
  if (sourceDashboards.length > 1)
    preview.blocked.push(
      'Multiple dashboards use the old purpose. Review these custom worklists before replacement.',
    );
  const sourceDashboard = sourceDashboards[0];
  const sourceColumns =
    from === 'review'
      ? reviewColumns.map((column) => column.code)
      : ['statusCode', 'queueCode'];
  if (sourceDashboard) {
    const installed = select('platform', 'view', sourceViewCode);
    if (!sameCapabilityReference(sourceDashboard.view, installed))
      preview.blocked.push(
        'The purpose dashboard has an incompatible saved view contract.',
      );
    if (
      unknownKeys(sourceDashboard, [
        'id',
        'label',
        'view',
        'scope',
        'columns',
        'filterFields',
        'defaults',
        'summaries',
        'quickActions',
      ]).length ||
      unknownReferenceSettings(sourceDashboard.view) ||
      unknownKeys(sourceDashboard.defaults, [
        'nodeId',
        'page',
        'pageSize',
        'status',
        'sortBy',
        'descending',
      ]).length ||
      sourceDashboard.scope !== 'workflow-instance' ||
      !Array.isArray(sourceDashboard.columns) ||
      sourceDashboard.columns.some((code) => !sourceColumns.includes(code)) ||
      !sameCodes(sourceDashboard.filterFields, ['status']) ||
      !sameCodes(sourceDashboard.summaries, ['totalCount']) ||
      !Array.isArray(sourceDashboard.quickActions) ||
      sourceDashboard.quickActions.length
    ) {
      preview.blocked.push(
        'The purpose dashboard has custom fields, filters, actions or settings. They will not be discarded by changing purpose.',
      );
    }
  }
  const removedCommands = declaration.behavior.commands.map(
    (command) => command.capability,
  );
  for (const dashboard of dashboards.filter(
    (item) => item !== sourceDashboard,
  )) {
    if (!dashboard || !Array.isArray(dashboard.quickActions)) {
      preview.blocked.push(
        'An additional dashboard has malformed saved actions. Review them before changing purpose.',
      );
    } else if (
      dashboard.quickActions.some(
        (action) =>
          !action?.key ||
          removedCommands.some((reference) =>
            sameCapabilityReference(action, reference),
          ),
      )
    ) {
      preview.blocked.push(
        `Dashboard '${dashboard.label || dashboard.id}' uses an action from the old purpose. Update that action before changing purpose.`,
      );
    }
  }
  const interaction = declaration.detail.mainInteraction;
  if (
    interaction &&
    (interaction.code !== 'generated-command-form' ||
      interaction.version !== 1 ||
      unknownKeys(interaction, ['code', 'version', 'command']).length ||
      unknownReferenceSettings(interaction.command) ||
      !removedCommands.some((reference) =>
        sameCapabilityReference(interaction.command, reference),
      ))
  ) {
    preview.blocked.push(
      'The detail page has a custom main interaction. It must be reviewed before changing purpose.',
    );
  }
  if (preview.blocked.length) return preview;
  const outgoing = document.edges.filter((edge) => edge?.fromNodeId === nodeId);
  const route =
    outgoing.length === 1
      ? document.nodes.find(
          (candidate) =>
            candidate?.id === outgoing[0].toNodeId &&
            candidate.typeCode === 'routing_decision' &&
            candidate.routing?.conditionCode === 'review.result' &&
            candidate.routing.conditionVersion === 1,
        )
      : null;
  const outcomes = ['approved', 'changes_required'].map((code) => {
    const edges = route
      ? document.edges.filter(
          (edge) => edge?.fromNodeId === route.id && edge.outcomeCode === code,
        )
      : [];
    const destination =
      edges.length === 1
        ? document.nodes.filter(
            (candidate) =>
              candidate?.id === edges[0].toNodeId &&
              ['human_step', 'automated_step', 'terminal'].includes(candidate.typeCode),
          )
        : [];
    return {
      code,
      label: code === 'approved' ? 'Approved' : 'Changes required',
      reasonPolicy: (code === 'approved' ? 'optional' : 'required') as
        | 'optional'
        | 'required',
      destinationNodeId: destination.length === 1 ? destination[0].id : '',
    };
  });
  const commands =
    target === 'review'
      ? [
          {
            capability: select(
              'workflow',
              'command',
              'workflow.review.request',
            ),
            requirements: [],
            outcomes: [],
          },
          {
            capability: select('workflow', 'command', 'workflow.review.submit'),
            requirements: [...REVIEW_REQUIREMENTS],
            outcomes,
          },
        ]
      : [
          {
            capability: select(
              'workflow',
              'command',
              'workflow.preparation.submit',
            ),
            requirements: [],
            outcomes: [],
          },
        ];
  const targetView = select('platform', 'view', purposeView[target]);
  const targetColumns =
    target === 'review'
      ? reviewColumns.map((column) => column.code)
      : ['1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(document.payloadSchemaVersion ?? '')
        ? ['statusCode']
        : ['statusCode', 'queueCode'];
  const columns =
    sourceDashboard?.columns.filter((code) => targetColumns.includes(code)) ??
    [];
  const defaultLabel =
    from === 'review' ? 'Review records' : 'Preparation and corrections';
  const targetDashboard = {
    id: sourceDashboard?.id ?? availableDashboardId(dashboards, target),
    label:
      sourceDashboard && sourceDashboard.label !== defaultLabel
        ? sourceDashboard.label
        : target === 'review'
          ? 'Review records'
          : 'Preparation and corrections',
    view: targetView,
    scope: 'workflow-instance',
    columns: columns.length
      ? columns
      : target === 'review'
        ? ['decisionRecordId', 'statusCode']
        : targetColumns,
    filterFields: ['status'],
    defaults:
      target === 'review'
        ? {
            page: 1,
            pageSize: 25,
            status: null,
            sortBy: 'decisionRecordId',
            descending: false,
          }
        : { nodeId, page: 1, pageSize: 25, status: null },
    summaries: ['totalCount'],
    quickActions: [],
  };
  const nextDeclaration: WorkflowStepDeclaration = {
    ...declaration,
    behavior: { ...declaration.behavior, commands },
    detail: {
      ...declaration.detail,
      ...(interaction
        ? {
            mainInteraction: {
              ...interaction,
              command: commands[commands.length - 1].capability,
            },
          }
        : {}),
    },
    dashboards: sourceDashboard
      ? dashboards.map((dashboard) =>
          dashboard === sourceDashboard ? targetDashboard : dashboard,
        )
      : [...dashboards, targetDashboard],
  };
  preview.replaced.push(
    `${from === 'review' ? 'Request review and submit review' : 'Submit preparation'} actions, their requirements and outcomes are replaced by the installed ${target} actions.`,
  );
  if (from === 'review')
    preview.replaced.push(
      `Review outcome labels and reason rules: ${declaration.behavior.commands
        .find(
          (command) => command.capability.key.code === 'workflow.review.submit',
        )!
        .outcomes.map(
          (outcome) => `${outcome.label} (${outcome.reasonPolicy} reason)`,
        )
        .join('; ')}.`,
    );
  preview.replaced.push(
    'The purpose dashboard switches worklist, keeps supported columns, and resets its filter, page and sorting defaults. Its identity and custom label are retained.',
  );
  if (interaction)
    preview.replaced.push(
      'The generated main command form switches to the new purpose action.',
    );
  if (
    target === 'review' &&
    outcomes.some((outcome) => !outcome.destinationNodeId)
  )
    preview.unresolved.push(
      'Choose both review outcome destinations after applying. Existing paths are retained; the draft is incomplete until routing is configured.',
    );
  if (target === 'preparation') {
    const destinations =
      outgoing.length === 1 && outgoing[0].pathKindCode !== 'return'
        ? document.nodes.filter(
            (candidate) => candidate?.id === outgoing[0].toNodeId,
          )
        : [];
    const destination =
      destinations.length === 1 && destinations[0].typeCode === 'human_step'
        ? destinations[0]
        : null;
    const reviewPaths = document.edges.filter(
      (edge) => edge?.fromNodeId === destination?.id,
    );
    const reviewRoute =
      reviewPaths.length === 1 &&
      document.nodes.some(
        (candidate) =>
          candidate?.id === reviewPaths[0].toNodeId &&
          candidate.typeCode === 'routing_decision' &&
          candidate.routing?.conditionCode === 'review.result' &&
          candidate.routing.conditionVersion === 1,
      );
    const installedReceiver =
      installedStepPurpose(destination) === 'review' &&
      destination?.declaration?.behavior.commands.every((command) => {
        const installed = catalog.filter(
          (entry) =>
            entry.key.provider === 'workflow' &&
            entry.key.kind === 'command' &&
            entry.key.code === command.capability.key.code &&
            entry.key.version === 1 &&
            entry.lifecycle === 'available',
        );
        return (
          installed.length === 1 &&
          sameCapabilityReference(
            command.capability,
            installedReference(installed[0]),
          )
        );
      });
    if (!installedReceiver || !reviewRoute)
      preview.unresolved.push(
        'Connect this preparation step to a configured review step. Existing conditional paths are retained; inspect them in Open Paths before reconnecting.',
      );
    const incoming = document.nodes.filter(
      (candidate) =>
        installedStepPurpose(candidate) === 'preparation' &&
        document.edges.some(
          (edge) =>
            edge?.fromNodeId === candidate.id && edge.toNodeId === nodeId,
        ),
    );
    for (const source of incoming)
      preview.unresolved.push(
        `Preparation step '${source.displayName || source.stepCode || source.id}' currently sends work here. It will need a different configured review destination.`,
      );
  }
  if (declaration.detail.blocks.length)
    preview.unresolved.push(
      `Inspect the ${declaration.detail.blocks.length} retained detail block(s) for the new purpose; their settings have not been changed.`,
    );
  if (dashboards.length > (sourceDashboard ? 1 : 0))
    preview.unresolved.push(
      'Inspect the retained additional dashboards for compatibility with the new purpose.',
    );
  preview.document = replacePresentation(updated, nodeId, nextDeclaration);
  return preview;
}

function unknownKeys(value: unknown, known: string[]): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    return ['malformed settings'];
  return Object.keys(value).filter((key) => !known.includes(key));
}
function sameCodes(actual: unknown, expected: readonly string[]): boolean {
  return (
    Array.isArray(actual) &&
    actual.length === expected.length &&
    expected.every(
      (code) => actual.filter((value) => value === code).length === 1,
    )
  );
}
function unknownReferenceSettings(
  reference: WorkflowCapabilityReference | null | undefined,
): boolean {
  return (
    unknownKeys(reference, [
      'key',
      'inputSchemaHash',
      'outputSchemaHash',
      'contractHash',
    ]).length > 0 ||
    unknownKeys(reference?.key, ['provider', 'kind', 'code', 'version'])
      .length > 0
  );
}
function availableDashboardId(
  dashboards: NonNullable<WorkflowStepDeclaration['dashboards']>,
  purpose: WorkflowInstalledPurpose,
): string {
  let id: string = purpose,
    suffix = 2;
  while (dashboards.some((dashboard) => dashboard?.id === id))
    id = `${purpose}-${suffix++}`;
  return id;
}

export const reviewColumns = [
  { code: 'decisionRecordId', label: 'Review' },
  { code: 'statusCode', label: 'Status' },
  { code: 'notes', label: 'Notes (protected)' },
  { code: 'workSummary', label: 'Submitted work (protected)' },
];
export function initializeReviewPresentation(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  catalog: WorkflowCapabilityCatalogEntry[],
): WorkflowDefinitionDocument {
  const node = document.nodes.find((n) => n.id === nodeId);
  if (node?.typeCode !== 'human_step') throw new Error('Select a human step.');
  if (
    ![
      '1.3',
      '1.4',
      '1.5',
      '1.6',
      '1.7',
      '1.8',
      '1.9',
      '1.10',
      '1.11',
      '1.12',
      '1.13',
      '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(document.payloadSchemaVersion)
  )
    throw new Error(
      'Enable editable routing using the setup action above before adding pages.',
    );
  let updated: WorkflowDefinitionDocument = {
    ...document,
    payloadSchemaVersion: requireWorkflowPayloadVersion(
      document.payloadSchemaVersion,
      '1.7',
    ),
    triggers: document.triggers ?? [],
    numbering: document.numbering ?? { mode: 'disabled' },
  };
  const select = (provider: string, kind: string, code: string) => {
    const entries = catalog.filter(
      (e) =>
        e.key.provider === provider &&
        e.key.kind === kind &&
        e.key.code === code &&
        e.key.version === 1,
    );
    if (entries.length !== 1)
      throw new Error(`The installed ${code} contract is unavailable.`);
    updated = addWorkflowCapability(updated, entries[0], catalog);
    return installedReference(entries[0]);
  };
  let declaration = node.declaration;
  if (!declaration) {
    const successor = document.edges.filter((e) => e.fromNodeId === nodeId);
    const route = document.nodes.find(
      (n) =>
        n.id === successor[0]?.toNodeId &&
        n.typeCode === 'routing_decision' &&
        n.routing?.conditionCode === 'review.result' &&
        n.routing.conditionVersion === 1,
    );
    if (successor.length !== 1 || !route)
      throw new Error(
        'Apply both review outcome routes in Step behavior first.',
      );
    const outcomes = ['approved', 'changes_required'].map((code) => {
      const paths = document.edges.filter(
        (e) => e.fromNodeId === route.id && e.outcomeCode === code,
      );
      if (paths.length !== 1)
        throw new Error('Apply one destination for each review outcome first.');
      return {
        code,
        label: code === 'approved' ? 'Approved' : 'Changes required',
        reasonPolicy: (code === 'approved' ? 'optional' : 'required') as
          | 'optional'
          | 'required',
        destinationNodeId: paths[0].toNodeId,
      };
    });
    declaration = {
      behavior: {
        assignmentPolicyId: null,
        commands: [
          {
            capability: select(
              'workflow',
              'command',
              'workflow.review.request',
            ),
            requirements: [],
            outcomes: [],
          },
          {
            capability: select('workflow', 'command', 'workflow.review.submit'),
            requirements: [
              'review.current-request',
              'review.independent-reviewer',
              'review.changes-reason',
            ],
            outcomes,
          },
        ],
      },
      detail: {
        layout: 'workspace.two-column.v1',
        contextPlacement: 'side',
        blocks: [],
      },
      dashboards: [],
    };
  }
  if (!declaration.dashboards?.length) {
    declaration = {
      ...declaration,
      dashboards: [
        {
          id: 'reviews',
          label: 'Review records',
          view: select('platform', 'view', 'workflow.review.records'),
          scope: 'workflow-instance',
          columns: ['decisionRecordId', 'statusCode'],
          filterFields: ['status'],
          defaults: {
            page: 1,
            pageSize: 25,
            status: null,
            sortBy: 'decisionRecordId',
            descending: false,
          },
          summaries: ['totalCount'],
          quickActions: [],
        },
      ],
    };
  }
  return replacePresentation(updated, nodeId, declaration);
}
export function replacePresentation(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  declaration: WorkflowStepDeclaration,
): WorkflowDefinitionDocument {
  return {
    ...document,
    nodes: document.nodes.map((n) =>
      n.id === nodeId ? { ...n, declaration } : n,
    ),
  };
}

/** Preparation is a declared business operation with exactly one installed review successor. */
export function initializePreparationPresentation(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  catalog: WorkflowCapabilityCatalogEntry[],
): WorkflowDefinitionDocument {
  const node = document.nodes.find((n) => n.id === nodeId);
  const edges = document.edges.filter((e) => e.fromNodeId === nodeId);
  const next = document.nodes.find((n) => n.id === edges[0]?.toNodeId);
  if (
    node?.typeCode !== 'human_step' ||
    node.declaration ||
    edges.length !== 1 ||
    edges[0].pathKindCode === 'return' ||
    next?.typeCode !== 'human_step' ||
    !['workflow.review.request', 'workflow.review.submit'].every((code) =>
      next.declaration?.behavior.commands.some(
        (c) => c.capability.key.code === code,
      ),
    )
  )
    throw new Error(
      'Preparation needs a single path to a configured independent review step. Configure that review first.',
    );
  let updated: WorkflowDefinitionDocument = {
    ...document,
    payloadSchemaVersion: requireWorkflowPayloadVersion(
      document.payloadSchemaVersion,
      '1.7',
    ),
    triggers: document.triggers ?? [],
    numbering: document.numbering ?? { mode: 'disabled' as const },
  };
  const select = (provider: string, kind: string, code: string) => {
    const entries = catalog.filter(
      (e) =>
        e.key.provider === provider &&
        e.key.kind === kind &&
        e.key.code === code &&
        e.key.version === 1,
    );
    if (entries.length !== 1)
      throw new Error(`The installed ${code} contract is unavailable.`);
    updated = addWorkflowCapability(updated, entries[0], catalog);
    return installedReference(entries[0]);
  };
  const declaration: WorkflowStepDeclaration = {
    behavior: {
      assignmentPolicyId:
        document.assignmentPolicies?.find((p) => p?.nodeId === nodeId)?.id ??
        null,
      commands: [
        {
          capability: select(
            'workflow',
            'command',
            'workflow.preparation.submit',
          ),
          requirements: [],
          outcomes: [],
        },
      ],
    },
    detail: {
      layout: 'workspace.two-column.v1',
      contextPlacement: 'side',
      blocks: [],
    },
    dashboards: [
      {
        id: 'preparation',
        label: 'Preparation and corrections',
        view: select('platform', 'view', 'workflow.preparation.work'),
        scope: 'workflow-instance',
        columns: ['1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(document.payloadSchemaVersion ?? '')
          ? ['statusCode']
          : ['statusCode', 'queueCode'],
        filterFields: ['status'],
        defaults: { nodeId, page: 1, pageSize: 25, status: null },
        summaries: ['totalCount'],
        quickActions: [],
      },
    ],
  };
  return replacePresentation(updated, nodeId, declaration);
}

export function hasEditableRouting(
  document: WorkflowDefinitionDocument,
): boolean {
  return [
    '1.3',
    '1.4',
    '1.5',
    '1.6',
    '1.7',
    '1.8',
    '1.9',
    '1.10',
    '1.11',
    '1.12',
    '1.13',
    '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(document.payloadSchemaVersion);
}

/** Explicit opt-in for an older draft; never rewrites routes or published work. */
export function enableEditableRouting(
  document: WorkflowDefinitionDocument,
): WorkflowDefinitionDocument {
  if (hasEditableRouting(document)) return document;
  if (!['1.0', '1.1', '1.2'].includes(document.payloadSchemaVersion))
    throw new Error(
      'This draft format is not supported by this editor. Its saved settings have been preserved.',
    );
  return { ...document, payloadSchemaVersion: '1.3' };
}

export function connectPreparationReview(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  reviewNodeId: string,
): WorkflowDefinitionDocument {
  const source = document.nodes.find((n) => n.id === nodeId);
  const target = document.nodes.find((n) => n.id === reviewNodeId);
  const paths = document.edges.filter((e) => e.fromNodeId === nodeId);
  if (
    !hasEditableRouting(document) ||
    source?.typeCode !== 'human_step' ||
    target?.typeCode !== 'human_step' ||
    nodeId === reviewNodeId
  )
    throw new Error('Choose a different work step to review this submission.');
  if (
    paths.length > 1 ||
    paths.some((e) => e.outcomeCode || e.pathKindCode === 'return') ||
    paths.some(
      (e) =>
        document.nodes.find((n) => n.id === e.toNodeId)?.typeCode ===
        'routing_decision',
    )
  )
    throw new Error(
      'This step has conditional paths. Open Paths to review them before choosing a review step.',
    );
  const id = paths[0]?.id ?? `${nodeId}-submit-for-review`;
  if (!paths.length && document.edges.some((e) => e.id === id))
    throw new Error(
      'A path with this identity already exists. Open Paths to resolve it.',
    );
  return {
    ...document,
    edges: [
      ...document.edges.filter((e) => e.fromNodeId !== nodeId),
      { id, fromNodeId: nodeId, toNodeId: reviewNodeId },
    ],
  };
}
